import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_TOKEN, ApiMessageResult } from '@aksia/infrastructure';
import { ToasterService } from '@aksia/ui';
import { map } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';

@Component({
  selector: 'app-forgot-password',
  imports: [],
  templateUrl: './forgot-password.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordPageContent {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly httpClient = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT_TOKEN);
  private readonly toaster = inject(ToasterService);
  protected readonly currentYear = new Date().getFullYear();

  protected readonly username = signal('');
  protected readonly submitted = signal(false);
  protected readonly attemptedSubmit = signal(false);
  protected readonly inFlight = signal(false);
  protected readonly usernameTouched = signal(false);
  protected readonly formShaking = signal(false);

  protected readonly returnUrl = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('returnUrl') ?? undefined)),
    { initialValue: undefined as string | undefined },
  );

  @HostBinding('class')
  protected readonly hostClass = 'auth-page';

  private readonly formUrlEncodedHeaders = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded',
  });

  protected updateUsername(value: string) {
    this.username.set(value);
    this.submitted.set(false);
  }

  protected showUsernameError() {
    return (
      (this.attemptedSubmit() || this.usernameTouched()) &&
      !this.username().trim()
    );
  }

  private triggerShake() {
    this.formShaking.set(true);
    setTimeout(() => this.formShaking.set(false), 400);
  }

  protected requestReset(event?: Event) {
    event?.preventDefault();
    this.attemptedSubmit.set(true);
    if (!this.username().trim()) {
      this.triggerShake();
      return;
    }

    this.inFlight.set(true);
    const url = `${this.environment.microservices_api_url}/account/forgot-password`;
    const body = new HttpParams().set('username', this.username().trim());

    this.httpClient
      .post<ApiMessageResult>(url, body.toString(), {
        headers: this.formUrlEncodedHeaders,
      })
      .subscribe({
        next: () => {
          this.inFlight.set(false);
          this.submitted.set(true);
        },
        error: (err: HttpErrorResponse) => {
          this.inFlight.set(false);
          if (err.status === 429) {
            this.toaster.error(
              'Too many requests. Please wait a few minutes and try again.',
            );
          } else if (err.status >= 500) {
            const correlationId = err.error?.correlation_id as
              string | undefined;
            this.toaster.error(
              correlationId
                ? `Something went wrong. Please try again later. Reference: ${correlationId}`
                : 'Something went wrong. Please try again later.',
            );
          } else {
            // Anti-enumeration: treat all other 4xx as success
            this.submitted.set(true);
          }
        },
      });
  }

  protected goToLogin() {
    const returnUrl = this.returnUrl();
    this.router.navigate(
      ['/login'],
      returnUrl ? { queryParams: { returnUrl } } : {},
    );
  }
}
